/**
 * src/components/ui/Tabs.jsx — a tab list and its panels.
 *
 * Follows the WAI-ARIA APG "Tabs" pattern: `role="tablist"`/`"tab"`/
 * `"tabpanel"`, roving tabindex (only the active tab is in the normal tab
 * order; Tab from the tablist jumps straight to the active panel, not
 * through every tab), and Left/Right arrow keys to move between tabs
 * (Home/End to jump to the first/last). Manual activation — arrowing moves
 * focus without switching the panel until Enter/Space, per the APG's
 * "manual activation" variant, since switching content on every arrow
 * press is disorienting when panels hold real content, not single words.
 */
import { useRef } from 'react';

/**
 * @param {{
 *   label: string,
 *   tabs: { id: string, label: string, panel: React.ReactNode }[],
 *   active: string,
 *   onChange: (id: string) => void,
 * }} props
 * @returns {JSX.Element}
 */
export function Tabs({ label, tabs, active, onChange }) {
  const tabRefs = useRef({});

  const move = (fromIndex, delta) => {
    const nextIndex = (fromIndex + delta + tabs.length) % tabs.length;
    const next = tabs[nextIndex];
    tabRefs.current[next.id]?.focus();
  };

  const onKeyDown = (e, index) => {
    if (e.key === 'ArrowRight') {
      e.preventDefault();
      move(index, 1);
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      move(index, -1);
    } else if (e.key === 'Home') {
      e.preventDefault();
      tabRefs.current[tabs[0].id]?.focus();
    } else if (e.key === 'End') {
      e.preventDefault();
      tabRefs.current[tabs[tabs.length - 1].id]?.focus();
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onChange(tabs[index].id);
    }
  };

  const activeTab = tabs.find((t) => t.id === active) ?? tabs[0];

  return (
    <div className="ui-tabs">
      <div role="tablist" aria-label={label} className="ui-tablist">
        {tabs.map((tab, index) => {
          const selected = tab.id === activeTab.id;
          return (
            <button
              key={tab.id}
              ref={(el) => {
                tabRefs.current[tab.id] = el;
              }}
              type="button"
              role="tab"
              id={`tab-${tab.id}`}
              aria-selected={selected}
              aria-controls={`tabpanel-${tab.id}`}
              tabIndex={selected ? 0 : -1}
              className={`ui-tab${selected ? ' ui-tab--active' : ''}`}
              onClick={() => onChange(tab.id)}
              onKeyDown={(e) => onKeyDown(e, index)}
            >
              {tab.label}
            </button>
          );
        })}
      </div>
      {tabs.map((tab) => (
        <div
          key={tab.id}
          role="tabpanel"
          id={`tabpanel-${tab.id}`}
          aria-labelledby={`tab-${tab.id}`}
          hidden={tab.id !== activeTab.id}
          tabIndex={0}
          className="ui-tabpanel"
        >
          {tab.panel}
        </div>
      ))}
    </div>
  );
}
